import { useAppSelector } from '@customer/store/hooks';
import styles from './InsightsCards.module.scss';
import { useGetHealthSiteOverviewQuery } from '@customer/store/apis/healthApi';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import MetricCard from '@customer/components/data-display/metric-card/MetricCard';
import { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import { SelectionTimeRange } from '@customer/store/slices/globalSlice';
import { useGetInteractionsSummaryQuery } from '@customer/store/apis/interactionsApi';
import { Permission, userHasPermission } from '@customer/permissions';

const OVERVIEW_TIMERANGE = SelectionTimeRange.Hours24;
const INTERACTIONS_TIMERANGE = SelectionTimeRange.Days7;

export function InsightsCards() {
  const { t } = useTranslation();
  const { getTimeRangeText } = useLocaleFormatter();

  const userData = useAppSelector(state => state.auth);

  const { data: healthOverview } = useGetHealthSiteOverviewQuery(
    {
      query: { timeRange: OVERVIEW_TIMERANGE },
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: typeof userData.siteId === 'undefined',
    },
  );

  const { data: interactionsSummary } = useGetInteractionsSummaryQuery(
    {
      query: { timeRange: INTERACTIONS_TIMERANGE },
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: typeof userData.siteId === 'undefined',
    },
  );

  return (
    <div className={styles.cards}>
      <MetricCard
        title={`${t('SelfTest')} - ${getTimeRangeText(OVERVIEW_TIMERANGE, t)}`}
        to="/health/self-test"
        metrics={[
          {
            label: (
              <>
                {t('AssetsWithLevelFaults', { level: 1 })}
                <br />
                {t('TotalAndCurrentlyActive')}
              </>
            ),
            dataPrimary: healthOverview?.assetsL1FaultTotal,
            dataSecondary: healthOverview?.assetsL1FaultActive,
            isFraction: false,
          },
          {
            label: (
              <>
                {t('AssetsWithLevelFaults', { level: 2 })}
                <br />
                {t('TotalAndCurrentlyActive')}
              </>
            ),
            dataPrimary: healthOverview?.assetsL2FaultTotal,
            dataSecondary: healthOverview?.assetsL2FaultActive,
            isFraction: false,
          },
        ]}
      />

      {userHasPermission(userData, Permission.PerformanceRead) && (
        <MetricCard
          title={`${t('Interactions')} - ${getTimeRangeText(INTERACTIONS_TIMERANGE, t)}`}
          to="/performance"
          metrics={[
            {
              label: (
                <>
                  {t('VehicleInteraction')}
                  <br />
                  {t('Total')}
                </>
              ),
              dataPrimary: interactionsSummary?.viTot,
            },
            {
              label: (
                <>
                  {t('GeofenceInteraction')}
                  <br />
                  {t('Total')}
                </>
              ),
              dataPrimary: interactionsSummary?.giTot,
            },
          ]}
        />
      )}

      <MetricCard
        title={t('Configuration')}
        to="/health/configuration"
        metrics={[
          {
            label: t('MetricAssetsOutdatedSoftware'),
            dataPrimary: healthOverview?.assetsConfigOutdated,
            dataSecondary: healthOverview?.totalAssets,
            isFraction: true,
          },
          {
            label: t('MetricAssetsNameMismatch'),
            dataPrimary: healthOverview?.assetsNameMismatch,
            dataSecondary: healthOverview?.totalAssets,
            isFraction: true,
          },
        ]}
      />

      <MetricCard
        title={t('ReportingStatus')}
        to="/health/reporting"
        metrics={[
          {
            label: t('MetricAssetsConnectedBad'),
            dataPrimary: healthOverview?.assetsReportedRed,
            dataSecondary: healthOverview?.totalAssets,
            isFraction: true,
            status: BadgeStatus.Danger,
          },
          {
            label: t('MetricAssetsConnectedModerate'),
            dataPrimary: healthOverview?.assetsReportedYellow,
            dataSecondary: healthOverview?.totalAssets,
            isFraction: true,
            status: BadgeStatus.Caution,
          },
        ]}
      />
    </div>
  );
}

export default InsightsCards;
